import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Manage the impact slider movement and autoplay.
 * Translation: Manage the impact slider movement and autoplay.
 *
 * @param {number} cardCount - Total number of cards.
 * @returns {Object} Carousel state and controls.
 */
const useImpactCarousel = (cardCount) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    /**
     * Derive the visible cards per row from the viewport width.
     * Translation: Derive the visible cards per row from the viewport width.
     */
    const handleResize = () => {
      const width = window.innerWidth;
      let nextItemsPerView = 4;

      if (width < 768) {
        nextItemsPerView = 1;
      } else if (width < 1200) {
        nextItemsPerView = 2;
      }

      setItemsPerView(nextItemsPerView);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = useMemo(
    () => Math.max(cardCount - itemsPerView, 0),
    [cardCount, itemsPerView]
  );
  const safeActiveIndex = Math.min(activeIndex, maxIndex);

  useEffect(() => {
    if (maxIndex === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) =>
        currentIndex >= maxIndex ? 0 : currentIndex + 1
      );
    }, 4000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [maxIndex]);

  /**
   * Advance the carousel by one step.
   * Translation: Advance the carousel by one step.
   */
  const nextSlide = useCallback(() => {
    setActiveIndex((currentIndex) =>
      currentIndex >= maxIndex ? 0 : currentIndex + 1
    );
  }, [maxIndex]);

  /**
   * Reverse the carousel by one step.
   * Translation: Reverse the carousel by one step.
   */
  const previousSlide = useCallback(() => {
    setActiveIndex((currentIndex) =>
      currentIndex <= 0 ? maxIndex : currentIndex - 1
    );
  }, [maxIndex]);

  const trackStyle = useMemo(() => {
    const slideWidthPercent = 100 / cardCount;
    const transform = `translateX(-${safeActiveIndex * slideWidthPercent}%)`;

    return {
      transform,
    };
  }, [cardCount, safeActiveIndex]);

  return {
    activeIndex: safeActiveIndex,
    itemsPerView,
    nextSlide,
    previousSlide,
    trackStyle,
    maxIndex,
  };
};

export default useImpactCarousel;
